import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Play } from "lucide-react";
import api, { apiError } from "../lib/api";
import { userName, useUsers } from "../lib/hooks";
import { Chip, KpiCard, PageHeader } from "../components/common";
import { Button } from "../components/ui/button";
import { COMMITMENT_STATUS, DECISION_EXEC_STATUS, OPEN_QUESTION_STATUS, TASK_STATUS, fmtDate } from "../lib/labels";
import { AlertOctagon, Clock, FileWarning, GitBranch, ListChecks, MessageCircleQuestion, Scale, ShieldAlert } from "lucide-react";

const ATENCION = [
  ["actas_pendientes", "Actas pendientes", FileWarning, "reuniones", "sin_acta"],
  ["acciones_vencidas", "Acciones vencidas", Clock, "acciones", "vencidas"],
  ["acciones_bloqueadas", "Acciones bloqueadas", AlertOctagon, "acciones", "bloqueadas"],
  ["decisiones_sin_ejecutar", "Decisiones sin ejecutar", GitBranch, "decisiones", null],
  ["cuestiones_sin_responder", "Cuestiones sin responder", MessageCircleQuestion, "cuestiones", "sin_responder"],
  ["compromisos_incumplidos", "Compromisos incumplidos", Scale, "compromisos", "incumplido"],
  ["procesos_bloqueados", "Procesos dependientes bloqueados", ShieldAlert, "procesos", "activo"],
  ["reuniones_sin_preparar", "Reuniones próximas sin preparar", ListChecks, "reuniones", "sin_preparar"],
];
const ACCIONES = [
  ["abiertas", "Abiertas", null], ["vencidas", "Vencidas", "vencidas"], ["proximas", "Próximas (7d)", "proximas"],
  ["sin_evidencia", "Sin evidencia", "sin_evidencia"], ["en_revision", "En revisión", "en_revision"],
  ["completadas_mes", "Completadas (mes)", "completadas_mes"], ["arrastradas_repetidas", "Arrastradas >1 reunión", "arrastradas_repetidas"],
];
const DECISIONES = [
  ["confirmadas", "Confirmadas", null], ["pendientes_ejecucion", "Pendientes de ejecución", "pendiente_ejecucion"],
  ["bloqueadas", "Bloqueadas", "bloqueada"], ["ejecutadas", "Ejecutadas", "ejecutada"], ["pendientes_verificacion", "Pendientes de verificación", "ejecutada"],
];
const CALIDAD = [
  ["reuniones_sin_acta", "Reuniones sin acta", "reuniones", "sin_acta"],
  ["dias_medios_aprobacion", "Días medios hasta aprobación", null, null],
  ["dias_medios_distribucion", "Días medios hasta distribución", null, null],
  ["acciones_sin_responsable", "Acciones sin responsable", "acciones", "sin_responsable"],
  ["cerradas_fuera_de_plazo", "Acciones cerradas fuera de plazo", "acciones", "fuera_de_plazo"],
  ["rebotes_distribucion", "Rebotes de distribución", null, null],
  ["objeciones_materiales", "Objeciones materiales", null, null],
];

export default function ReunionesDashboard() {
  const { map } = useUsers();
  const qc = useQueryClient();
  const [drill, setDrill] = useState(null); // {kind, filter, label}
  const { data: d, error } = useQuery({ queryKey: ["reuniones-dashboard"], queryFn: async () => (await api.get("/reuniones/seguimiento/cuadro-de-mando")).data, retry: false });
  const { data: items } = useQuery({
    queryKey: ["reuniones-drill", drill?.kind, drill?.filter],
    queryFn: async () => (await api.get("/reuniones/seguimiento/cuadro-de-mando/items", { params: { kind: drill.kind, filter: drill.filter || undefined } })).data,
    enabled: !!drill,
  });
  const evalMut = useMutation({
    mutationFn: async () => (await api.post("/reuniones/seguimiento/evaluar")).data,
    onSuccess: (s) => { toast.success(`Automatizaciones: ${s.recordatorios} recordatorios, ${s.alertas} alertas, ${s.escalaciones} escalaciones`); qc.invalidateQueries({ queryKey: ["reuniones-dashboard"] }); },
    onError: (e) => toast.error(apiError(e) || "Error"),
  });

  if (error) return <div className="p-8" data-testid="reuniones-dash-denied"><p className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">{apiError(error)}</p></div>;
  if (!d) return <div className="p-8 text-sm text-slate-500">Cargando…</div>;

  const kpi = (key, label, icon, kind, filter, group) => (
    <button key={`${group}-${key}`} onClick={() => kind && setDrill({ kind, filter, label })}
      className={`ts-card p-3 text-left transition-colors ${kind ? "ts-card-hover" : "cursor-default opacity-90"}`} data-testid={`kpi-${group}-${key}`}>
      <p className="ts-caption">{label}</p>
      <p className={`font-display text-2xl font-bold ${d[group][key] ? "text-slate-900" : "text-slate-500"}`}>{d[group][key] ?? "—"}</p>
      {kind && <p className="text-[10px] text-blue-600">drill-down →</p>}
    </button>
  );

  const itemText = (it) => it.title || it.question || it.text || it.subject || it.id;
  const itemStatus = (it) => it.exec_status || it.status;
  const itemLink = (it) => it.meeting_id ? `/reuniones/${it.meeting_id}/seguimiento` : (it.id?.startsWith("mtg") ? `/reuniones` : "#");

  return (
    <div className="space-y-5" data-testid="reuniones-dashboard">
      <Link to="/reuniones" className="flex items-center gap-1 text-xs text-blue-700 hover:underline" data-testid="back-to-meetings"><ArrowLeft className="h-3 w-3" /> Volver a reuniones</Link>
      <PageHeader title="Cuadro de mando de reuniones" subtitle="KPI → listado filtrado → elemento → fuente → acción. Umbrales: recordatorio 3d antes, alerta 3d, escalación 7d (configurables)."
        testid="reuniones-dash-title"
        actions={<Button variant="outline" onClick={() => evalMut.mutate()} disabled={evalMut.isPending} data-testid="run-automations"><Play className="mr-1 h-4 w-4" /> Evaluar recordatorios y escalaciones</Button>} />

      <section data-testid="dash-atencion">
        <h2 className="ts-section-title mb-2">Requiere atención</h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{ATENCION.map(([k, l, i, kind, f]) => kpi(k, l, i, kind, f, "atencion"))}</div>
      </section>
      <section data-testid="dash-acciones">
        <h2 className="ts-section-title mb-2">Acciones</h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">{ACCIONES.map(([k, l, f]) => kpi(k, l, null, "acciones", f, "acciones"))}</div>
      </section>
      <section data-testid="dash-decisiones">
        <h2 className="ts-section-title mb-2">Decisiones</h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">{DECISIONES.map(([k, l, f]) => kpi(k, l, null, "decisiones", f, "decisiones"))}</div>
      </section>
      <section data-testid="dash-calidad">
        <h2 className="ts-section-title mb-2">Calidad del proceso</h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">{CALIDAD.map(([k, l, kind, f]) => kpi(k, l, null, kind, f, "calidad"))}</div>
      </section>

      {drill && (
        <section className="rounded-md border border-blue-200 bg-white p-4" data-testid="drill-panel">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-800" data-testid="drill-title">{drill.label} ({items?.items?.length ?? "…"})</h2>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setDrill(null)} data-testid="drill-close">Cerrar</Button>
          </div>
          <div className="mt-2 divide-y divide-slate-100">
            {(items?.items || []).map((it) => (
              <Link key={it.id} to={itemLink(it)} className="flex items-center gap-2 py-2 text-sm text-slate-700 hover:text-blue-700" data-testid={`drill-item-${it.id}`}>
                <span className="min-w-0 flex-1 truncate">{itemText(it)}</span>
                {it.assignee_id || it.exec_responsible_id || it.responsible_id ? <span className="text-xs text-slate-400">{userName(map, it.assignee_id || it.exec_responsible_id || it.responsible_id)}</span> : null}
                {it.due_date && <span className="text-xs text-slate-400">{fmtDate(it.due_date)}</span>}
                {itemStatus(it) && <Chip map={{ ...TASK_STATUS, ...DECISION_EXEC_STATUS, ...OPEN_QUESTION_STATUS, ...COMMITMENT_STATUS }} value={itemStatus(it)} />}
              </Link>
            ))}
            {items && !items.items.length && <p className="py-3 text-xs text-slate-400">Sin elementos en este filtro.</p>}
          </div>
        </section>
      )}
    </div>
  );
}
